import {
  daysHeld,
  deleteBooking,
  deleteStop,
  heldBy,
  setTransfer,
  updateBooking,
  updateStop,
  type Booking,
  type Plan,
  type PlanStop,
  live,
} from '@trippath/shared';

import { BookingDialog } from '../booking/BookingDialog';
import type { Sector } from '../booking/TicketEditor';
import { StopDialog } from './StopDialog';
import { cents, stopInputOf } from '../../lib/stopInput';
import { wayClockIn } from '../../lib/wayClock';

interface Props {
  plan: Plan;
  stop: PlanStop;
  onClose: () => void;
  onSaved: (next: Plan) => void;
}

/** A stop and its way on changed in a dialog, from the plan or any page that shows it. */
export function EditStop({ plan, stop, onClose, onSaved }: Props) {
  return (
    <StopDialog
      trip={plan.trip.id}
      currency={plan.trip.currency}
      stop={stop}
      // As it is now: changed by someone else while it is open, it says so.
      latest={plan.stops.find((one) => one.id === stop.id) ?? null}
      // Its way's times while it was not chosen go with it: a way chosen here keeps them.
      save={(input, transfer) =>
        updateStop(plan.trip.id, stop.id, { ...input, wayTimes: stop.wayTimes }).then((next) =>
          // Only a change on the way, or a stop again, when that changed.
          transfer === stop.transfer ? next : setTransfer(plan.trip.id, stop.id, transfer),
        )
      }
      daysHeld={daysHeld(plan, stop)}
      {...(heldBy(plan, stop) === null
        ? { remove: () => deleteStop(plan.trip.id, stop.id) }
        : {})}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

/**
 * A booking of a stop changed in a dialog: a ticket for its way says when it leaves and arrives,
 * kept on the way too.
 */
export function ChangeBooking({
  plan,
  stop,
  booking,
  onClose,
  onSaved,
  sector = null,
}: Props & { booking: Booking; sector?: Sector | null }) {
  const ticket =
    booking.kind === 'ticket' && live(booking) && stop.out
      ? { out: stop.out, clock: wayClockIn(plan, stop) }
      : null;
  return (
    <BookingDialog
      plan={plan}
      booking={{ booking, stop: stop.id }}
      {...(ticket ? { way: ticket.clock } : {})}
      save={(input, times) => {
        const kept = updateBooking(plan.trip.id, booking.id, input);
        return ticket
          ? updateStop(
              plan.trip.id,
              stop.id,
              stopInputOf(stop, {
                way: {
                  estimate: cents(ticket.out.estimate),
                  ...ticket.clock.kept(times),
                  link: ticket.out.link,
                },
              }),
            ).then(() => kept)
          : kept;
      }}
      remove={() => deleteBooking(plan.trip.id, booking.id)}
      onClose={onClose}
      onSaved={onSaved}
      sector={sector}
    />
  );
}
